<template>
  <Modal :title="title">
    <QForm>
      <QSelect
        v-model="duplicateTo"
        dense
        emit-value
        filled
        label="Duplicate request to"
        map-options
        :options="requestTypes"
        square
      />
      <div
        v-if="duplicateTo"
        class="q-gutter-md q-mt-lg details"
      >
        <p>
          <strong> {{ duplicateTo }} request details: </strong>
        </p>
        <AllUserDropdown
          clearable
          :emit-value="false"
          label="Requested by"
          :model-value="item.user"
          :rules="[
            (val) => (val && val.id > 0) || 'Must select a requesting user',
          ]"
          @update:model-value="setUser"
        />

        <QInput
          v-model="userOrganisation"
          filled
          label="Organisation"
          readonly
          square
          type="text"
        />
        <QInput
          v-model="sourceName"
          filled
          label="Source"
          readonly
          square
          type="text"
        />

        <div class="q-my-md q-mx-none q-ml-lg">
          <p>
            <strong>Start Time:</strong> {{ displayTimestamp(item.startTime) }}
          </p>
          <p><strong>End Time:</strong> {{ displayTimestamp(item.endTime) }}</p>
          <p><strong>Duration:</strong> {{ getDuration }}</p>
        </div>

        <QInput
          v-model="item.title"
          filled
          label="Title"
          square
          type="text"
        />
        <QInput
          v-model="notes"
          filled
          label="Notes"
          square
          type="textarea"
        />
        <div class="row justify-between">
          <QCheckbox
            v-model="item.billable"
            label="Billable"
          />
        </div>
      </div>
    </QForm>
    <template #buttons="{ close }">
      <QBtn
        class="q-mr-md text-black"
        flat
        label="Cancel"
        @click="close"
      />
      <QBtn
        class="no-shadow bg-primary bold q-py-smd text-white"
        :disabled="!duplicateTo"
        label="Create"
        @click="createItem"
      />
    </template>
  </Modal>
</template>

<script>
import { storeToRefs } from "pinia";

import Modal from "shared/components/core/modals/Modal.vue";
import { formatIntlDate, getTimeFromDuration } from "shared/helpers/date";
import { MediaRequest } from "shared/resources";
import TranscriptRequest from "shared/resources/TranscriptRequest";

import AllUserDropdown from "src/components/common/AllUserDropdown.vue";
import { useUserStore } from "src/stores/user";

export default {
  name: "DuplicateMediaOrTranscriptRequestModal",
  components: {
    AllUserDropdown,
    Modal,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    type: {
      type: String,
      default: null,
    },
  },
  emits: ["reload"],
  setup() {
    const userStore = useUserStore();
    const { currentUser } = storeToRefs(userStore);

    return { currentUser };
  },
  data() {
    return {
      sourceName: "",
      duplicateTo: "",
      requestTypes: ["Transcript Request", "Media Item"],
    };
  },
  computed: {
    notes: {
      get() {
        return this.item.notes;
      },
      set(value) {
        this.item.notes = value;

        return value;
      },
    },
    getDuration() {
      if (this.type === "Transcript Request") {
        return getTimeFromDuration(this.item.endTime - this.item.startTime);
      }

      return getTimeFromDuration(
        this.item.endTime / 1000 - this.item.startTime / 1000
      );
    },
    title() {
      return `Duplicate ${this.type} request`;
    },
    userOrganisation() {
      return this.item.user?.organisation?.name;
    },
  },
  watch: {
    duplicateTo: {
      handler() {
        this.getConditionalFormData();
      },
    },
  },
  methods: {
    displayTimestamp(timestamp) {
      const timeZone =
        this.type === "Transcript Request"
          ? this.item.timeZone
          : this.item.source.timeZone;

      return formatIntlDate(timestamp, {
        dateStyle: "short",
        timeStyle: "medium",
        timeZone,
      });
    },
    getConditionalFormData() {
      this.sourceName = `${this.item.source.id} - ${this.item.source.name}`;
    },
    createItem() {
      if (this.duplicateTo === "Transcript Request") {
        this.createTranscriptRequest(this.item);
      } else {
        this.createMediaItemRequest(this.item);
      }
    },
    createMediaItemRequest(item) {
      const mediaItem = item;

      if (this.type === "Transcript Request") {
        mediaItem.startTime = item.startTime * 1000;
        mediaItem.endTime = item.endTime * 1000;
        mediaItem.id = null;
      } else {
        mediaItem.sourceId = item.source.id;
        mediaItem.sourceType = this.type;
      }

      mediaItem.accountManagerId = this.currentUser.id;
      mediaItem.status = "Incomplete";
      this.saveMediaItem(mediaItem);
    },
    createTranscriptRequest(item) {
      const transcriptRequest = item;

      if (this.type === "Transcript Request") {
        transcriptRequest.formats = transcriptRequest.formats
          ? transcriptRequest.formats
          : [];

        if (transcriptRequest.transcriptionMethod) {
          if (transcriptRequest.inProgress()) {
            transcriptRequest.transcriptionMethod = null;
          } else {
            transcriptRequest.originalId = item.id;
            transcriptRequest.secondaryAccountManagerId = this.currentUser.id;

            if (transcriptRequest.status === "Completed") {
              if (transcriptRequest.transcriptionMethod === "automated") {
                transcriptRequest.status = "Ready";
              } else if (
                transcriptRequest.transcriptionMethod === "professional"
              ) {
                transcriptRequest.status = "Sent for manual transcription";
              } else {
                transcriptRequest.status = "Incomplete";
              }
            }
          }
        }
      } else {
        transcriptRequest.sourceId = item.source.id;
        transcriptRequest.sourceType = this.type;
        transcriptRequest.startTime = item.startTime / 1000;
        transcriptRequest.endTime = item.endTime / 1000;
        transcriptRequest.formats = ["docx", "pdf"];
        transcriptRequest.timeZone = item.source.timeZone;
        transcriptRequest.status = "Incomplete";
      }

      transcriptRequest.fileUrl = [];
      transcriptRequest.recipients = [];
      transcriptRequest.id = null;
      transcriptRequest.accountManagerId = this.currentUser.id;
      this.saveTranscriptRequest(transcriptRequest);
    },
    async saveMediaItem(mediaItem) {
      try {
        this.$q.loading.show();

        const duplicateMediaRequest = new MediaRequest(mediaItem);
        await duplicateMediaRequest.save();

        this.$q
          .dialog({
            title: "Success!",
            message:
              "Your request has been successfully duplicated to a Media Item",
            persistent: true,
            ok: "Done",
          })
          .onOk(() => {
            this.$emit("reload");
          });
      } catch (error) {
        this.$error(
          "There was an error duplicating this to a Media Item. Please try again.",
          error
        );
      } finally {
        this.$q.loading.hide();
      }
    },
    async saveTranscriptRequest(transcriptRequest) {
      try {
        this.$q.loading.show();

        const newTranscriptRequest = new TranscriptRequest(transcriptRequest);

        await newTranscriptRequest.saveResource({
          with: ["manualAttachments", "transcriptionAttachments"],
        });

        this.$q
          .dialog({
            title: "Success!",
            message:
              "Your request has been successfully duplicated to a Transcript Request",
            persistent: true,
            ok: "Done",
          })
          .onOk(() => {
            this.$emit("reload");
          });
      } catch {
        this.$q.notify({
          message:
            "There was an error duplicating this to a Transcript Request. Please try again.",
          color: "grey-7",
          position: "bottom",
          closeBtn: "Dismiss",
        });
      } finally {
        this.$q.loading.hide();
      }
    },
    setUser(user) {
      this.item.user = user;

      if (user) {
        this.item.userId = user.id;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.streem-modal2) {
  width: 45%;
}

:deep(.q-field--with-bottom) {
  padding-bottom: 0;
}

.details {
  border-top: 1px solid #ddd;
}
</style>
